// Admin Panel — Studio IA Local (liste d'attente + parrainages + export CSV)
import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Download, RefreshCcw, Trash2, Trophy, Users, Calendar, Copy, Loader2 } from "lucide-react";

const PROFILE_LABELS = {
  light:   { label: "Légère",   color: "bg-emerald-500/20 text-emerald-200 border-emerald-400/40" },
  creator: { label: "Créateur", color: "bg-fuchsia-500/20 text-fuchsia-200 border-fuchsia-400/40" },
  full:    { label: "Complète", color: "bg-amber-500/20 text-amber-200 border-amber-400/40" },
  "":      { label: "?",        color: "bg-white/5 text-white/45 border-white/10" },
  "?":     { label: "?",        color: "bg-white/5 text-white/45 border-white/10" },
};

export const AdminStudioIALocalPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/admin/studio-ia-local/waitlist")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Chargement échoué"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const del = async (id, email) => {
    if (!window.confirm(`Supprimer ${email} de la liste d'attente ?`)) return;
    try {
      await api.delete(`/admin/studio-ia-local/waitlist/${id}`);
      toast.success("Supprimé");
      load();
    } catch { toast.error("Erreur"); }
  };

  const exportCSV = () => {
    // Fetch avec le token pour déclencher le download
    const token = localStorage.getItem("madafrica_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/studio-ia-local/waitlist/export`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `studio_ia_local_waitlist_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("CSV téléchargé");
      })
      .catch(() => toast.error("Erreur export"));
  };

  const copyEmails = () => {
    if (!data?.waitlist) return;
    const filtered = data.waitlist.filter(w => !q || w.email.toLowerCase().includes(q.toLowerCase()));
    navigator.clipboard.writeText(filtered.map(w => w.email).join("\n"))
      .then(() => toast.success(`${filtered.length} emails copiés`))
      .catch(() => toast.error("Erreur"));
  };

  if (loading || !data) {
    return (
      <div className="text-center py-16 text-white/50" data-testid="admin-studio-loading">
        <Loader2 size={28} className="animate-spin mx-auto mb-2" />
        Chargement de la liste d'attente…
      </div>
    );
  }

  const filtered = (data.waitlist || []).filter(w =>
    !q || w.email.toLowerCase().includes(q.toLowerCase())
       || (w.ref_code || "").includes(q.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="admin-studio-ia-local-panel">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <h2 className="text-xl font-black bg-gradient-to-r from-fuchsia-300 to-purple-300 bg-clip-text text-transparent">
            🧬 Studio IA Local
          </h2>
          <p className="text-xs text-white/50">Liste d'attente & analyse des parrainages</p>
        </div>
        <button onClick={load} data-testid="studio-admin-refresh" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold bg-white/[0.05] border border-white/15 hover:bg-white/[0.1]">
          <RefreshCcw size={12} /> Rafraîchir
        </button>
        <button onClick={copyEmails} data-testid="studio-admin-copy" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold bg-white/[0.05] border border-white/15 hover:bg-white/[0.1]">
          <Copy size={12} /> Copier emails
        </button>
        <button onClick={exportCSV} data-testid="studio-admin-export" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold bg-fuchsia-500/15 border border-fuchsia-400/40 text-fuchsia-100 hover:bg-fuchsia-500/25">
          <Download size={12} /> Export CSV
        </button>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-2xl border border-fuchsia-400/30 bg-fuchsia-500/[0.08] p-4">
          <p className="text-[10px] uppercase tracking-widest text-fuchsia-300 mb-1">Total inscrits</p>
          <p className="text-3xl font-black text-white tabular-nums">{data.total || 0}</p>
        </div>
        <div className="rounded-2xl border border-emerald-400/25 bg-emerald-500/[0.06] p-4">
          <p className="text-[10px] uppercase tracking-widest text-emerald-300 mb-1"><Calendar size={10} className="inline mr-1" /> Aujourd'hui</p>
          <p className="text-3xl font-black text-white tabular-nums">{data.today || 0}</p>
        </div>
        <div className="rounded-2xl border border-blue-400/25 bg-blue-500/[0.06] p-4">
          <p className="text-[10px] uppercase tracking-widest text-blue-300 mb-1">7 derniers jours</p>
          <p className="text-3xl font-black text-white tabular-nums">{data.week || 0}</p>
        </div>
        <div className="rounded-2xl border border-amber-400/25 bg-amber-500/[0.06] p-4">
          <p className="text-[10px] uppercase tracking-widest text-amber-300 mb-1"><Trophy size={10} className="inline mr-1" /> Top parrain</p>
          <p className="text-3xl font-black text-white tabular-nums">
            {data.top_refs?.[0]?.referrals || 0}
          </p>
        </div>
      </div>

      {/* Répartition par profil */}
      {data.by_profile?.length > 0 && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
          <p className="text-xs font-bold text-white/70 uppercase tracking-widest mb-3">📊 Répartition par profil</p>
          <div className="flex flex-wrap gap-2">
            {data.by_profile.map(p => {
              const meta = PROFILE_LABELS[p.profile] || PROFILE_LABELS[""];
              return (
                <div key={p.profile} className={`px-3 py-2 rounded-xl border ${meta.color} inline-flex items-center gap-2`}>
                  <span className="font-bold text-sm">{meta.label}</span>
                  <span className="text-lg font-black tabular-nums">{p.n}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Top parrains */}
      {data.top_refs?.length > 0 && (
        <div className="rounded-2xl border border-amber-400/25 bg-amber-500/[0.05] p-4">
          <p className="text-xs font-bold text-amber-200 uppercase tracking-widest mb-3">🏆 Top 10 parrains</p>
          <div className="space-y-1.5">
            {data.top_refs.map((r, i) => (
              <div key={r.ref_code} className="flex items-center gap-3 px-3 py-2 rounded-xl bg-black/30">
                <span className="w-6 h-6 rounded-full bg-amber-400/20 border border-amber-400/50 text-amber-200 text-xs font-black flex items-center justify-center">
                  {i + 1}
                </span>
                <span className="flex-1 text-sm text-white truncate">{r.email}</span>
                <span className="font-mono text-[10px] text-white/40">{r.ref_code}</span>
                <span className="text-sm font-black text-amber-200 tabular-nums">
                  <Users size={12} className="inline mr-1" /> {r.referrals}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recherche + liste */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <p className="text-xs font-bold text-white/70 uppercase tracking-widest flex-1">
            📧 Inscrits ({filtered.length}/{data.waitlist?.length || 0})
          </p>
          <input
            type="search"
            placeholder="Chercher par email ou code..."
            value={q}
            onChange={e => setQ(e.target.value)}
            data-testid="studio-admin-search"
            className="rounded-xl bg-black/50 border border-white/15 px-3 py-1.5 text-xs placeholder:text-white/30 w-full sm:w-64"
          />
        </div>

        {filtered.length === 0 ? (
          <p className="text-center py-8 text-white/40 text-sm italic">Aucun inscrit trouvé.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-white/50 border-b border-white/10">
                  <th className="text-left py-2 px-2">Email</th>
                  <th className="text-left py-2 px-2">Profil</th>
                  <th className="text-left py-2 px-2">Ref</th>
                  <th className="text-left py-2 px-2">Parrainé par</th>
                  <th className="text-center py-2 px-2">Parrainages</th>
                  <th className="text-left py-2 px-2">Inscription</th>
                  <th className="text-right py-2 px-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(w => {
                  const profileMeta = PROFILE_LABELS[w.profile || "?"] || PROFILE_LABELS[""];
                  return (
                    <tr key={w.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="py-2 px-2 text-white">{w.email}</td>
                      <td className="py-2 px-2">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${profileMeta.color}`}>
                          {profileMeta.label}
                        </span>
                      </td>
                      <td className="py-2 px-2 font-mono text-[10px] text-fuchsia-200">{w.ref_code || "—"}</td>
                      <td className="py-2 px-2 font-mono text-[10px] text-amber-200">{w.referred_by || "—"}</td>
                      <td className="py-2 px-2 text-center font-bold text-fuchsia-200">{w.referrals || 0}</td>
                      <td className="py-2 px-2 text-[10px] text-white/50">
                        {new Date(w.created_at + "Z").toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                      </td>
                      <td className="py-2 px-2 text-right">
                        <button
                          onClick={() => del(w.id, w.email)}
                          data-testid={`studio-admin-delete-${w.id}`}
                          className="p-1.5 rounded-lg bg-rose-500/10 hover:bg-rose-500/20 text-rose-300"
                          title="Supprimer"
                        >
                          <Trash2 size={12} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminStudioIALocalPanel;
